import React, { useState, useEffect } from 'react'
import { 
  Card, 
  Typography, 
  Button, 
  Spin, 
  Alert, 
  Descriptions, 
  Table, 
  Space, 
  Tag,
  Row,
  Col,
  Statistic,
  Divider,
  Grid
} from 'antd'
import { 
  ArrowLeftOutlined,
  CreditCardOutlined,
  FileTextOutlined,
  CalendarOutlined,
  DollarOutlined,
  PrinterOutlined,
  DownloadOutlined,
  SyncOutlined
} from '@ant-design/icons'
import { useParams, useNavigate } from 'react-router-dom'
import { customerApi } from '../services/customerApi'
import { formatPaymentTerms } from '@serviceflow/shared'
import dayjs from 'dayjs'

const { Title, Text } = Typography
const { useBreakpoint } = Grid

interface InvoiceItem {
  id: string
  name: string
  description?: string
  quantity: number
  unitPrice: number
  totalPrice: number
  taxRate?: number
  taxAmount?: number
}

interface InvoiceDetails {
  id: string
  invoiceNumber: string
  title?: string
  description?: string
  status: string
  subtotal: number
  taxAmount: number
  discountAmount: number
  totalAmount: number
  paidAmount: number
  balanceAmount: number
  issuedAt: string
  dueAt: string
  sentAt?: string
  paidAt?: string
  paymentTerms: string
  notes?: string
  terms?: string
  isRecurring?: boolean
  recurringPeriod?: string
  stripeSubscriptionLinkUrl?: string
  subscriptionStatus?: string
  job?: {
    id: string
    jobNumber: string
    title: string
    description?: string
  }
  business: {
    id: string
    name: string
    phone?: string
    email?: string
    address?: string
  }
  items: InvoiceItem[]
  createdAt: string
  updatedAt: string
}

const getStatusColor = (status: string) => {
  const colors = {
    'DRAFT': 'default',
    'SENT': 'processing',
    'PAID': 'success',
    'OVERDUE': 'error',
    'CANCELLED': 'default'
  }
  return colors[status] || 'default'
}

const getStatusText = (status: string) => {
  const statusMap = {
    'DRAFT': 'Draft',
    'SENT': 'Sent',
    'PAID': 'Paid',
    'OVERDUE': 'Overdue',
    'CANCELLED': 'Cancelled'
  }
  return statusMap[status] || status
}

export const InvoiceDetails: React.FC = () => {
  const { invoiceId } = useParams<{ invoiceId: string }>()
  const navigate = useNavigate()
  const screens = useBreakpoint()
  const isMobile = !screens.md // true for xs and sm screens
  const [invoice, setInvoice] = useState<InvoiceDetails | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    console.log('🔍 InvoiceDetails: Component mounted with invoiceId:', invoiceId)
    
    if (invoiceId) {
      loadInvoice(invoiceId)
    } else {
      console.log('❌ InvoiceDetails: No invoice ID provided')
    }
  }, [invoiceId])

  const loadInvoice = async (invoiceId: string) => {
    try {
      setLoading(true)
      setError(null)
      const invoiceData = await customerApi.getInvoice(invoiceId)
      setInvoice(invoiceData)
    } catch (err: any) {
      console.error('Failed to load invoice:', err)
      setError(err.response?.data?.message || 'Failed to load invoice details')
    } finally {
      setLoading(false)
    }
  }

  const handlePayNow = () => {
    if (invoiceId) {
      navigate(`/invoices/${invoiceId}/pay`)
    }
  }

  const columns = [
    {
      title: 'Item',
      dataIndex: 'name',
      key: 'name',
      render: (text: string, record: InvoiceItem) => (
        <div>
          <Text strong>{text}</Text>
          {record.description && (
            <div>
              <Text type="secondary" style={{ fontSize: '12px' }}>
                {record.description}
              </Text>
            </div>
          )}
        </div>
      )
    },
    {
      title: 'Quantity',
      dataIndex: 'quantity',
      key: 'quantity',
      width: 100,
      render: (quantity: number) => Number(quantity || 0).toFixed(2)
    },
    {
      title: 'Unit Price',
      dataIndex: 'unitPrice',
      key: 'unitPrice',
      width: 120,
      render: (price: number) => `$${Number(price || 0).toFixed(2)}`
    },
    {
      title: 'Total',
      dataIndex: 'totalPrice',
      key: 'totalPrice',
      width: 120,
      render: (total: number) => (
        <Text strong>${Number(total || 0).toFixed(2)}</Text>
      )
    }
  ]

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '50px' }}>
        <Spin size="large" />
        <p style={{ marginTop: '16px' }}>Loading invoice details...</p>
      </div>
    )
  }

  if (error) {
    return (
      <Alert
        message="Error Loading Invoice"
        description={error}
        type="error"
        showIcon
        action={
          <Space>
            <Button size="small" onClick={() => navigate('/invoices')}>
              Back to Invoices
            </Button>
            <Button size="small" type="primary" onClick={() => invoiceId && loadInvoice(invoiceId)}>
              Retry
            </Button>
          </Space>
        }
      />
    )
  }

  if (!invoice) {
    return (
      <Alert
        message="Invoice Not Found"
        description="The requested invoice could not be found."
        type="warning"
        showIcon
        action={
          <Button onClick={() => navigate('/invoices')}>
            Back to Invoices
          </Button>
        }
      />
    )
  }

  const canPay = invoice.status !== 'PAID' && invoice.status !== 'CANCELLED' && Number(invoice.balanceAmount || 0) > 0

  return (
    <div style={{ 
      maxWidth: '800px', 
      margin: '0 auto', 
      padding: isMobile ? '16px' : '24px',
      paddingBottom: canPay && isMobile ? '100px' : (isMobile ? '16px' : '24px') // Extra padding for sticky footer
    }}>
      {/* Header - Responsive */}
      <div style={{ marginBottom: '24px' }}>
        {/* Top row: Back button and Status */}
        <Row justify="space-between" align="middle" style={{ marginBottom: '12px' }}>
          <Col>
            <Button 
              icon={<ArrowLeftOutlined />} 
              onClick={() => navigate('/invoices')}
              size={isMobile ? 'middle' : 'middle'}
            >
              {isMobile ? 'Back' : 'Back to Invoices'}
            </Button>
          </Col>
          <Col>
            <Space>
              {invoice.isRecurring && (
                <Tag color="purple" icon={<SyncOutlined spin />}>
                  Recurring
                </Tag>
              )}
              <Tag color={getStatusColor(invoice.status)} style={{ margin: 0 }}>
                {getStatusText(invoice.status)}
              </Tag>
            </Space>
          </Col>
        </Row>
        
        {/* Title row */}
        <Title level={isMobile ? 3 : 2} style={{ margin: '0 0 12px 0' }}>
          Invoice {invoice.invoiceNumber}
        </Title>
        
        {/* Action buttons - hidden on mobile, shown on desktop */}
        {!isMobile && (
          <Space>
            <Button icon={<PrinterOutlined />}>
              Print
            </Button>
            <Button icon={<DownloadOutlined />}>
              Download PDF
            </Button>
            {canPay && (
              <Button 
                type="primary"
                icon={<CreditCardOutlined />}
                onClick={handlePayNow}
              >
                Pay Now
              </Button>
            )}
            {invoice.isRecurring && invoice.stripeSubscriptionLinkUrl && (
              <Button 
                type={invoice.subscriptionStatus === 'active' ? 'default' : 'primary'}
                icon={<SyncOutlined />}
                onClick={() => window.open(invoice.stripeSubscriptionLinkUrl, '_blank')}
              >
                {invoice.subscriptionStatus === 'active' ? 'Subscription Active' : 'Set Up Recurring'}
              </Button>
            )}
          </Space>
        )}
      </div>

      {/* Recurring Invoice Alert */}
      {invoice.isRecurring && (
        <Alert
          message="Recurring Invoice"
          description={
            invoice.subscriptionStatus === 'active' 
              ? `This is a recurring invoice billed ${invoice.recurringPeriod || 'monthly'}. Your payment method is set up for automatic payments.`
              : `This is a recurring invoice billed ${invoice.recurringPeriod || 'monthly'}. Set up automatic payments to never miss a payment.`
          }
          type={invoice.subscriptionStatus === 'active' ? 'success' : 'info'}
          showIcon
          icon={<SyncOutlined />}
          style={{ marginBottom: 24 }}
          action={
            invoice.stripeSubscriptionLinkUrl && invoice.subscriptionStatus !== 'active' && (
              <Button 
                size="small" 
                type="primary"
                onClick={() => window.open(invoice.stripeSubscriptionLinkUrl, '_blank')}
              >
                Set Up Auto-Pay
              </Button>
            )
          }
        />
      )}

      {/* Invoice Summary */}
      <Row gutter={[24, 24]} style={{ marginBottom: '24px' }}>
        <Col xs={24} sm={8}>
          <Card>
            <Statistic
              title="Total Amount"
              value={Number(invoice.totalAmount || 0)}
              precision={2}
              prefix={<DollarOutlined />}
              valueStyle={{ color: '#1890ff' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={8}>
          <Card>
            <Statistic
              title="Amount Paid"
              value={Number(invoice.paidAmount || 0)}
              precision={2}
              prefix={<DollarOutlined />}
              valueStyle={{ color: '#52c41a' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={8}>
          <Card>
            <Statistic
              title="Balance Due"
              value={Number(invoice.balanceAmount || 0)}
              precision={2}
              prefix={<DollarOutlined />}
              valueStyle={{ color: Number(invoice.balanceAmount || 0) > 0 ? '#faad14' : '#52c41a' }}
            />
          </Card>
        </Col>
      </Row>

      <Row gutter={[24, 24]}>
        {/* Left Column */}
        <Col xs={24} lg={16}>
          {/* Invoice Details */}
          <Card title="Invoice Information" style={{ marginBottom: '24px' }}>
            <Descriptions column={1}>
              {invoice.title && (
                <Descriptions.Item label="Title">
                  <Text strong>{invoice.title}</Text>
                </Descriptions.Item>
              )}
              
              {invoice.description && (
                <Descriptions.Item label="Description">
                  {invoice.description}
                </Descriptions.Item>
              )}

              <Descriptions.Item label="Issue Date">
                <Space>
                  <CalendarOutlined />
                  {dayjs(invoice.issuedAt).format('MMMM D, YYYY')}
                </Space>
              </Descriptions.Item>

              <Descriptions.Item label="Due Date">
                <Space>
                  <CalendarOutlined />
                  {dayjs(invoice.dueAt).format('MMMM D, YYYY')}
                </Space>
              </Descriptions.Item>

              {invoice.paidAt && (
                <Descriptions.Item label="Paid Date">
                  <Space>
                    <CalendarOutlined />
                    {dayjs(invoice.paidAt).format('MMMM D, YYYY')}
                  </Space>
                </Descriptions.Item>
              )}

              <Descriptions.Item label="Payment Terms">
                {formatPaymentTerms(invoice.paymentTerms)}
              </Descriptions.Item>
            </Descriptions>
          </Card>

          {/* Items */}
          <Card title="Invoice Items" style={{ marginBottom: '24px' }}>
            <Table
              columns={columns}
              dataSource={invoice.items}
              rowKey="id"
              pagination={false}
              summary={() => (
                <>
                  <Table.Summary.Row>
                    <Table.Summary.Cell index={0} colSpan={3}>
                      <Text strong>Subtotal</Text>
                    </Table.Summary.Cell>
                    <Table.Summary.Cell index={1}>
                      <Text strong>${Number(invoice.subtotal || 0).toFixed(2)}</Text>
                    </Table.Summary.Cell>
                  </Table.Summary.Row>
                  
                  {Number(invoice.discountAmount || 0) > 0 && (
                    <Table.Summary.Row>
                      <Table.Summary.Cell index={0} colSpan={3}>
                        <Text>Discount</Text>
                      </Table.Summary.Cell>
                      <Table.Summary.Cell index={1}>
                        <Text>-${Number(invoice.discountAmount || 0).toFixed(2)}</Text>
                      </Table.Summary.Cell>
                    </Table.Summary.Row>
                  )}
                  
                  {Number(invoice.taxAmount || 0) > 0 && (
                    <Table.Summary.Row>
                      <Table.Summary.Cell index={0} colSpan={3}>
                        <Text>Tax</Text>
                      </Table.Summary.Cell>
                      <Table.Summary.Cell index={1}>
                        <Text>${Number(invoice.taxAmount || 0).toFixed(2)}</Text>
                      </Table.Summary.Cell>
                    </Table.Summary.Row>
                  )}
                  
                  <Table.Summary.Row>
                    <Table.Summary.Cell index={0} colSpan={3}>
                      <Text strong style={{ fontSize: '16px' }}>Total</Text>
                    </Table.Summary.Cell>
                    <Table.Summary.Cell index={1}>
                      <Text strong style={{ fontSize: '16px' }}>${Number(invoice.totalAmount || 0).toFixed(2)}</Text>
                    </Table.Summary.Cell>
                  </Table.Summary.Row>
                </>
              )}
            />
          </Card>

          {/* Notes and Terms */}
          {(invoice.notes || invoice.terms) && (
            <Card title="Additional Information">
              {invoice.notes && (
                <div style={{ marginBottom: '16px' }}>
                  <Text strong>Notes:</Text>
                  <div>{invoice.notes}</div>
                </div>
              )}
              
              {invoice.terms && (
                <div>
                  <Text strong>Terms & Conditions:</Text>
                  <div>{invoice.terms}</div>
                </div>
              )}
            </Card>
          )}
        </Col>

        {/* Right Column */}
        <Col xs={24} lg={8}>
          {/* Related Job */}
          {invoice.job && (
            <Card title="Related Job" style={{ marginBottom: '24px' }}>
              <Space direction="vertical" style={{ width: '100%' }}>
                <div>
                  <FileTextOutlined style={{ marginRight: '8px' }} />
                  <Text strong>Job #{invoice.job.jobNumber}</Text>
                </div>
                <Text>{invoice.job.title}</Text>
                {invoice.job.description && (
                  <Text type="secondary">{invoice.job.description}</Text>
                )}
                <Button 
                  type="link" 
                  onClick={() => navigate(`/jobs/${invoice.job!.id}`)}
                  style={{ padding: 0 }}
                >
                  View Job Details
                </Button>
              </Space>
            </Card>
          )}

          {/* Business Information */}
          <Card title="From" style={{ marginBottom: '24px' }}>
            <Space direction="vertical" style={{ width: '100%' }}>
              <Text strong style={{ fontSize: '16px' }}>
                {invoice.business.name}
              </Text>
              
              {invoice.business.address && (
                <Text>{invoice.business.address}</Text>
              )}
              
              {invoice.business.phone && (
                <Text>{invoice.business.phone}</Text>
              )}
              
              {invoice.business.email && (
                <Text>{invoice.business.email}</Text>
              )}
            </Space>
          </Card>

          {/* Payment Action - Desktop only */}
          {canPay && !isMobile && (
            <Card>
              <div style={{ textAlign: 'center' }}>
                <Title level={4}>Ready to Pay?</Title>
                <Text type="secondary" style={{ display: 'block', marginBottom: '16px' }}>
                  Pay securely online with your credit card
                </Text>
                <Button 
                  type="primary" 
                  size="large"
                  icon={<CreditCardOutlined />}
                  onClick={handlePayNow}
                  block
                >
                  Pay ${Number(invoice.balanceAmount || 0).toFixed(2)} Now
                </Button>
              </div>
            </Card>
          )}
        </Col>
      </Row>

      {/* Sticky Pay Now Footer - Mobile Only */}
      {canPay && isMobile && (
        <div
          style={{
            position: 'fixed',
            bottom: 0,
            left: 0,
            right: 0,
            padding: '16px',
            background: '#fff',
            boxShadow: '0 -2px 8px rgba(0, 0, 0, 0.15)',
            zIndex: 1000,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: '12px'
          }}
        >
          <div style={{ flex: 1 }}>
            <Text type="secondary" style={{ fontSize: '12px', display: 'block' }}>
              Balance Due
            </Text>
            <Text strong style={{ fontSize: '18px', color: '#faad14' }}>
              ${Number(invoice.balanceAmount || 0).toFixed(2)}
            </Text>
          </div>
          <Button 
            type="primary" 
            size="large"
            icon={<CreditCardOutlined />}
            onClick={handlePayNow}
            style={{ 
              height: '48px',
              paddingLeft: '24px',
              paddingRight: '24px',
              fontSize: '16px'
            }}
          >
            Pay Now
          </Button>
        </div>
      )}
    </div>
  )
}
